// src/pages/IntensiveQuizPage.js
// Quiz page for Pre-Marriage Intensive modules (10 modules, 160 questions)
// Visual Theme: Emerald/Teal with Gold accents

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { INTENSIVE_MODULE_DATA } from '../data/intensiveModules';
import { getAnswerOptions } from '../utils/answerFormatHelper';
import { 
  initializeAssessment, 
  saveIntensiveAnswer, 
  calculateIntensiveModuleScore,
  completeIntensiveModule 
} from '../services/assessmentService';
import LoadingSpinner from '../components/LoadingSpinner';

const IntensiveQuizPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const moduleNumber = parseInt(searchParams.get('module')) || 1;

  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [moduleData, setModuleData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [canAccess, setCanAccess] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadQuizData();
  }, [currentUser, moduleNumber]);

  const loadQuizData = async () => {
    try {
      setLoading(true);

      // Get user data
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        navigate('/couple/dashboard');
        return;
      }

      const user = userDoc.data();
      setUserData(user);

      // Check if approved
      if (user.approvalStatus !== 'approved') {
        navigate('/couple/dashboard');
        return;
      }

      // Get couple data to check permissions
      const coupleDoc = await getDoc(doc(db, 'couples', user.coupleId));
      if (!coupleDoc.exists()) {
        navigate('/couple/dashboard');
        return;
      }

      const coupleData = coupleDoc.data();
      const permissions = coupleData.intensivePermissions || {};

      // Check if couple can take this module
      if (!permissions[moduleNumber]?.canTake) {
        alert('This module is not yet unlocked by your counselor.');
        navigate('/couple/dashboard');
        return;
      }

      // Get assessment data
      const assessmentDoc = await getDoc(doc(db, 'assessments', currentUser.uid));
      let assessmentData = null;

      if (assessmentDoc.exists()) {
        assessmentData = assessmentDoc.data();

        // Check if already completed
        if (assessmentData.completedIntensiveModules?.includes(moduleNumber)) {
          setAlreadyCompleted(true);
          setLoading(false);
          return;
        }

        // Load existing answers for this module
        const existingAnswers = {};
        const module = INTENSIVE_MODULE_DATA[moduleNumber];
        
        if (module && module.questions) {
          module.questions.forEach(q => {
            if (assessmentData.intensiveAnswers?.[q.id] !== undefined) {
              existingAnswers[q.id] = assessmentData.intensiveAnswers[q.id];
            }
          });
        }

        setAnswers(existingAnswers);
      } else {
        // Initialize assessment if doesn't exist
        const { success, error } = await initializeAssessment(
          currentUser.uid, 
          user.coupleId
        );
  
        if (!success) {
          console.error('Error initializing assessment:', error);
          alert('Error creating assessment. Please try again.');
          navigate('/couple/dashboard');
          return;
        }
      }

      // Load module data
      const module = INTENSIVE_MODULE_DATA[moduleNumber];
      if (!module) {
        alert('Module not found.');
        navigate('/couple/dashboard');
        return;
      }

      setModuleData(module);
      setCanAccess(true);
      setLoading(false);
    } catch (error) {
      console.error('Error loading quiz data:', error);
      alert('Error loading quiz. Please try again.');
      navigate('/couple/dashboard');
    }
  };

  const handleAnswer = async (value) => {
    try {
      setSaving(true);
      const currentQuestion = moduleData.questions[currentQuestionIndex];
      const questionId = currentQuestion.id;

      // Update local state
      const updatedAnswers = {
        ...answers,
        [questionId]: value
      };
      setAnswers(updatedAnswers);

      // Save to database
      await setDoc(
        doc(db, 'assessments', currentUser.uid),
        {
          [`intensiveAnswers.${questionId}`]: value,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );

      setSaving(false);

      // Move to next question or finish
      if (currentQuestionIndex < moduleData.questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        // Last question - calculate score and complete module
        await handleCompleteModule(updatedAnswers);
      }
    } catch (error) {
      console.error('Error saving answer:', error);
      setSaving(false);
      alert('Error saving answer. Please try again.');
    }
  };

  const handleCompleteModule = async (finalAnswers) => {
    try {
      setLoading(true);

      // Calculate module score (handles reverse scoring)
      const moduleScore = calculateIntensiveModuleScore(finalAnswers, moduleData.questions);

      // Complete the module
      const result = await completeIntensiveModule(
        currentUser.uid,
        moduleNumber,
        moduleScore,
        userData?.coupleId
      );

      if (result.success) {
        console.log(`✅ Module ${moduleNumber} completed with score: ${moduleScore}`);
        // Navigate to results page
        navigate(`/intensive/results/${moduleNumber}`);
      } else {
        alert('Error completing module: ' + result.error);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error completing module:', error);
      alert('Error completing module. Please try again.');
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < moduleData.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading module...</p>
        </div>
      </div>
    );
  }

  if (alreadyCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
          <div className="text-4xl sm:text-5xl md:text-6xl mb-4">✅</div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
            Module Already Completed
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6">
            You have already completed Module {moduleNumber}: {INTENSIVE_MODULE_DATA[moduleNumber]?.shortName}. 
            Your counselor is reviewing your responses.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate(`/intensive/results/${moduleNumber}`)}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
            >
              View Results
            </button>
            <button
              onClick={() => navigate('/couple/dashboard')}
              className="w-full py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!canAccess || !moduleData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 p-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8 text-center">
          <div className="text-4xl sm:text-5xl md:text-6xl mb-4">🔒</div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
            Access Denied
          </h2>
          <p className="text-sm sm:text-base text-gray-600 mb-6">
            You don't have permission to access this module yet.
          </p>
          <button
            onClick={() => navigate('/couple/dashboard')}
            className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = moduleData.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / moduleData.questions.length) * 100;
  const currentAnswer = answers[currentQuestion.id];
  const allAnswered = moduleData.questions.every(q => answers[q.id] !== undefined);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-3 sm:p-4 md:p-6">
      <div className="max-w-3xl mx-auto py-4 sm:py-6 md:py-8">
        {/* Header */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-5 md:p-6 mb-4 sm:mb-6 border-l-4 border-emerald-600">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">{moduleData.icon}</span>
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                  Module {moduleNumber} of 10
                </span>
              </div>
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">
                {moduleData.name}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                Question {currentQuestionIndex + 1} of {moduleData.questions.length}
              </p>
            </div>
            <button
              onClick={() => navigate('/couple/dashboard')}
              className="px-3 sm:px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors text-sm sm:text-base whitespace-nowrap"
            >
              Exit
            </button>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3">
            <div
              className="bg-gradient-to-r from-emerald-600 to-teal-600 h-2 sm:h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Navigation Dots */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex justify-center gap-1.5 sm:gap-2 flex-wrap">
            {moduleData.questions.map((q, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestionIndex(index)}
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm font-medium transition ${
                  answers[q.id] !== undefined
                    ? 'bg-emerald-500 text-white'
                    : currentQuestionIndex === index
                      ? 'bg-emerald-600 text-white ring-2 ring-emerald-300'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-5 sm:p-6 md:p-8">
          {/* Question Text */}
          <div className="mb-6 sm:mb-8">
            <div className="text-xs sm:text-sm text-emerald-600 font-medium mb-2">
              Question {currentQuestionIndex + 1}
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 leading-relaxed">
              {currentQuestion.text}
            </h2>
            {currentQuestion.reverseScored && (
              <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded inline-block">
                ⚡ This question is reverse-scored
              </div>
            )}
          </div>

          {/* Answer Options */}
          <div className="space-y-2 sm:space-y-3">
            {getAnswerOptions(currentQuestion).map((option) => (
              <button
                key={option.value}
                onClick={() => handleAnswer(option.value)}
                disabled={saving}
                className={`w-full p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 text-left font-medium transition-all ${
                  currentAnswer === option.value
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                    : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-gray-700'
                } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm sm:text-base">{option.text}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs sm:text-sm text-gray-500">
                      {option.value} pt{option.value !== 1 ? 's' : ''}
                    </span>
                    {currentAnswer === option.value && (
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-6 sm:mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              className="w-full sm:flex-1 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              ← Previous
            </button>
            {currentQuestionIndex === moduleData.questions.length - 1 ? (
              <button
                onClick={() => handleCompleteModule(answers)}
                disabled={!allAnswered || saving}
                className={`w-full sm:flex-1 py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                  allAnswered && !saving
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {saving ? 'Saving...' : allAnswered ? '✓ Submit Module' : `Answer all (${Object.keys(answers).length}/${moduleData.questions.length})`}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="w-full sm:flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors text-sm sm:text-base"
              >
                Next →
              </button>
            )}
          </div>

          {/* Why This Matters - Insight */}
          {currentQuestion.insight && (
            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs sm:text-sm text-amber-800">
                💡 <strong>Why this matters:</strong> {currentQuestion.insight}
              </p>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-4 sm:mt-6 bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-5 md:p-6">
          <h3 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">📋 Instructions</h3>
          <ul className="text-xs sm:text-sm text-gray-600 space-y-1">
            <li>• Answer all {moduleData.questions.length} questions in this module</li>
            <li>• Click on the numbered circles above to jump to any question</li>
            <li>• Green circles = answered, Current = highlighted</li>
            <li>• Your responses are saved automatically</li>
            <li>• Some questions may be reverse-scored (higher disagreement = better)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default IntensiveQuizPage;
